import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { Trophy, Megaphone, Calendar, UserCheck, AlertTriangle, ChevronRight, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

// Hard-coded launch announcement window — once per day until lock
const BONUS_LOCK_LABEL = "Thursday, 18th June 2026 at 5:00 PM UAE time";
const BONUS_LOCK_ISO = "2026-06-18T13:00:00+00:00"; // 5pm UAE = 13:00 UTC

export default function AnnouncementModal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    // Don't show if already past the lock
    if (Date.now() >= new Date(BONUS_LOCK_ISO).getTime()) return;
    // Don't show on the bonus page itself (user is already there)
    if (location.pathname === "/bonus") return;
    // Don't show if user has rename warning (let that flow finish first)
    if (user.rename_warning_at) return;
    // Once per day per user — only the FIRST login of the day triggers it
    const today = new Date().toISOString().slice(0, 10);
    const key = `mm_anno_seen_${user.id}_${today}`;
    let alreadySeen = false;
    try { alreadySeen = !!localStorage.getItem(key); } catch { /* ignore */ }
    if (alreadySeen) return;

    // GATE: wait until the user has completed (or skipped) onboarding.
    // OnboardingGate writes `mm_onboarded_${user.id} = "1"` on close.
    // For brand-new signups that flag is missing — we poll until it appears.
    let cancelled = false;
    const tryShow = () => {
      if (cancelled) return;
      let onboarded = false;
      let onboardingActive = false;
      try {
        onboarded = localStorage.getItem(`mm_onboarded_${user.id}`) === "1";
        onboardingActive = !!sessionStorage.getItem("mm_show_onboarding");
      } catch { /* ignore */ }
      if (!onboarded || onboardingActive) {
        // Onboarding still in flight — check again shortly
        setTimeout(tryShow, 500);
        return;
      }
      // Onboarding is fully done — small settle delay then show
      setTimeout(() => {
        if (!cancelled) setOpen(true);
      }, 600);
    };
    // Initial delay so we don't fight any page transitions
    const initial = setTimeout(tryShow, 800);
    return () => { cancelled = true; clearTimeout(initial); };
  }, [user, location.pathname]);

  const dismiss = () => {
    if (user) {
      const today = new Date().toISOString().slice(0, 10);
      try { localStorage.setItem(`mm_anno_seen_${user.id}_${today}`, "1"); } catch (e) { /* ignore */ }
    }
    setOpen(false);
  };

  const goPredict = () => {
    dismiss();
    navigate("/bonus");
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] grid place-items-center p-4 modal-backdrop"
          data-testid="announcement-modal"
          onClick={dismiss}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 14 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 8 }}
            transition={{ duration: 0.25 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg bg-surface border border-purple/40 rounded-3xl overflow-hidden shadow-[0_24px_80px_-12px_rgba(160,64,192,0.55)]"
          >
            {/* Hero header */}
            <div className="relative px-7 pt-8 pb-7 text-center bg-gradient-to-br from-vivid via-purple to-plum">
              <button
                onClick={dismiss}
                aria-label="Close"
                data-testid="anno-close-x"
                className="absolute top-4 right-4 w-9 h-9 rounded-full bg-black/30 hover:bg-black/50 text-white grid place-items-center"
              >
                <X size={16}/>
              </button>
              <div className="mx-auto w-16 h-16 rounded-full bg-black/30 border border-lavender/50 grid place-items-center mb-4 shadow-[0_0_24px_rgba(232,180,240,0.45)]">
                <Trophy className="text-lavender" size={28}/>
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-black/30 border border-lavender/40 text-lavender text-[10px] tracking-[0.3em] uppercase font-bold mb-3">
                <Megaphone size={12}/> Important Announcement
              </div>
              <div className="font-display text-3xl sm:text-4xl uppercase tracking-tight leading-tight text-white">
                Last Chance for<br/><span className="text-lavender">Bonus Predictions</span>
              </div>
              <p className="text-white/75 text-sm mt-3">Don&apos;t miss your shot at glory</p>
            </div>

            {/* Body */}
            <div className="p-6 sm:p-7 space-y-5">
              <div className="flex items-start gap-3 p-4 rounded-2xl bg-lavender/10 border border-lavender/30">
                <Calendar className="text-lavender shrink-0 mt-0.5" size={20}/>
                <div className="text-sm leading-relaxed text-white">
                  Bonus Predictions will be <span className="text-lavender font-bold">locked on {BONUS_LOCK_LABEL}</span>. Make your picks before the window closes!
                </div>
              </div>

              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-3">Also Important</div>
                <div className="rounded-2xl bg-black/20 border border-white/10 divide-y divide-white/10">
                  <div className="flex items-start gap-3 p-4">
                    <UserCheck className="text-lavender shrink-0 mt-0.5" size={20}/>
                    <div className="text-sm leading-relaxed text-white/80">
                      <span className="text-white font-bold">Make sure your profile shows your real name</span> so we can distribute prizes. Head to your profile and tap <span className="text-lavender font-bold">&ldquo;Edit name&rdquo;</span> to update it.
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4">
                    <AlertTriangle className="text-red-400 shrink-0 mt-0.5" size={20}/>
                    <div className="text-sm leading-relaxed text-white/80">
                      Any account that <span className="text-white font-bold">cannot be matched to the Momentum employee list</span> will be <span className="text-red-400 font-bold">suspended</span>.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer actions */}
            <div className="px-6 sm:px-7 pb-6 grid grid-cols-2 gap-3">
              <button
                onClick={dismiss}
                data-testid="anno-dismiss"
                className="btn-bounce w-full px-4 py-3 rounded-full bg-surface border border-white/15 text-white/80 hover:text-white hover:border-white/30 font-bold uppercase tracking-wider text-xs transition-colors"
              >
                Dismiss
              </button>
              <button
                onClick={goPredict}
                data-testid="anno-go-predict"
                className="btn-bounce cta-sweep inline-flex items-center justify-center gap-1.5 w-full px-4 py-3 rounded-full bg-gradient-to-r from-vivid to-purple hover:from-purple hover:to-lavender hover:text-plum text-white font-bold uppercase tracking-wider text-xs transition-all shadow-[0_4px_18px_rgba(160,64,192,0.45)]"
              >
                Make my predictions <ChevronRight size={14}/>
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
